/**
 * Upload Documents Page - Premium UI
 */

import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./UploadDocumentsPage.css";
import { documentsAPI, ordersAPI } from "../../services/api";
import { isAuthenticated } from "../../services/api";
import { useToast } from "../../Components/Toast/Toast";
import { validateMeetingExcel } from "../../utils/excelValidator";

const UploadDocumentsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const [services, setServices] = useState([]);
  const [order, setOrder] = useState(null);
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState({});

  // const orders = async () => {
  //   const response = await documentsAPI.getForOrder(location.state?.orderId);
  //   console.log("orders", response.data.inputDocuments);
  //   setUploadedDocuments(response.data.inputDocuments);
  // };
  // useEffect(() => {
  //   orders();
  // }, []);

  // Prevent duplicate initialization
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login");
      return;
    }

    // Prevent duplicate initialization
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    if (location.state?.services) {
      setServices(location.state.services);
      setOrder(location.state.order);
    } else if (location.state?.service) {
      setServices([location.state.service]);
      setOrder(location.state.order);
    } else if (location.state?.order) {
      setOrder(location.state.order);
      setServices(
        location.state.order.items
          ?.map((item) => item.service)
          .filter(Boolean) || [],
      );
    } else {
      navigate("/services");
    }

    // Load existing documents if managing existing files
    
    if (location.state?.manageExisting && location.state?.order) {
      loadExistingDocuments(location.state.order._id);
    }
  }, [location.state, navigate]);

  // Load existing documents
  const loadExistingDocuments = async (orderId) => {
    try {
      const response = await documentsAPI.getOrderDocuments(orderId);
      if (response.success) {
        // Get the order to check quantity limits
        const orderResponse = await ordersAPI.getOrderById(orderId);
        const currentOrder = orderResponse.data.order;


        // Map existing documents to the uploadedDocuments format
        const existingDocs = {};
        const serviceDocCounts = {}; // Track documents per service/document

        response.data.inputDocuments?.forEach((doc, index) => {
          const serviceId = doc.service;
          const documentSlot = doc.documentSlot;
          const serviceDocKey = `${serviceId}-${documentSlot}`;

          // Get quantity limit for this service
          const orderItem = currentOrder?.items?.find(item => item.service._id === serviceId);
          const maxQuantity = orderItem?.quantity || 1;
          // Initialize counter for this service/document
          if (!serviceDocCounts[serviceDocKey]) {
            serviceDocCounts[serviceDocKey] = 0;
          }

          // Only include documents within the quantity limit
          if (serviceDocCounts[serviceDocKey] < maxQuantity) {
            const quantityIndex = serviceDocCounts[serviceDocKey]; // Use current count as index
            const key = `${serviceId || 'unknown'}-${documentSlot || 'unknown'}-${quantityIndex}`;
            existingDocs[key] = {
              id: doc._id,
              name: doc.originalName,
              size: doc.fileSize,
              status: doc.status,
              serviceId: serviceId,
              documentSlot: documentSlot,
              quantityIndex: quantityIndex,
            };
            serviceDocCounts[serviceDocKey]++;
          } else {
          }
        });

        setUploadedDocuments(existingDocs);
      }
    } catch (error) {
      console.error("Error loading existing documents:", error);
      toast.error("Failed to load existing documents");
    }
  };
  const handleFileUpload = async (
    serviceId,
    documentSlot,
    documentType,
    file,
    quantityIndex = 0, // Add quantity index to track multiple uploads
  ) => {
    const service = services.find((s) => s._id === serviceId);

    // Get the quantity for this service from the order
    const orderItem = order?.items?.find(item => item.service._id === serviceId);
    const maxQuantity = orderItem?.quantity || 1;

    // Check if user has already uploaded the maximum allowed files for this service
    const existingUploads = Object.keys(uploadedDocuments)
      .filter(key => key.startsWith(`${serviceId}-${documentSlot}`))
      .length;

    if (existingUploads >= maxQuantity) {
      toast.error(`Maximum ${maxQuantity} file(s) allowed for ${service.name}. You have already uploaded ${existingUploads} file(s).`);
      return;
    }

    // Excel validation
    if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
      const validation = await validateMeetingExcel(file, service.slug);
      if (!validation.valid) {
        const uploadKey = `${serviceId}-${documentSlot}-${quantityIndex}`;

        setUploadErrors((prev) => ({
          ...prev,
          [uploadKey]: validation.message,
        }));

        toast.error(validation.message);
        return;
      }
    }
    if (!service) {
      toast.error("Service not found");
      return;
    }

    const requiredDoc = service.requiredDocuments?.find(
      (doc) => doc.name === documentSlot,
    );
    if (!requiredDoc) {
      toast.error("Invalid document type");
      return;
    }

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (
      requiredDoc.acceptedFormats &&
      !requiredDoc.acceptedFormats.includes(fileExtension)
    ) {
      toast.error(
        `Invalid file format. Accepted formats: ${requiredDoc.acceptedFormats.join(", ")}`,
      );
      return;
    }

    const maxSizeBytes = (requiredDoc.maxSize || 10) * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      toast.error(
        `File size exceeds limit. Maximum allowed: ${requiredDoc.maxSize || 10}MB`,
      );
      return;
    }

    const uploadKey = `${serviceId}-${documentSlot}-${quantityIndex}`;
    setUploading((prev) => ({ ...prev, [uploadKey]: true }));

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentType", documentType);
      formData.append("documentSlot", documentSlot);
      formData.append("serviceId", serviceId); // Add serviceId to link document to service
      formData.append("quantityIndex", quantityIndex); // Add quantity index to track multiple uploads

      if (order) {
        formData.append("orderId", order._id);
      }

      const response = await documentsAPI.uploadDocument(formData);

      if (response.success) {
        setUploadErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[uploadKey];
          return newErrors;
        });
        setUploadedDocuments((prev) => ({
          ...prev,
          [uploadKey]: {
            id: response.data.document._id,
            name: response.data.document.originalName,
            size: response.data.document.fileSize,
            status: response.data.document.status,
            serviceId: serviceId,
            documentSlot: documentSlot,
            quantityIndex: quantityIndex,
          },
        }));
        toast.success(`Uploaded ${file.name} (${existingUploads + 1}/${maxQuantity})`);
      } else {
        toast.error(response.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading((prev) => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleFileChange = (serviceId, documentSlot, documentType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Get the quantity for this service from the order
    const orderItem = order?.items?.find(item => item.service._id === serviceId);
    const maxQuantity = orderItem?.quantity || 1;

    // Find the next available quantity index for this service/document
    const existingUploads = Object.keys(uploadedDocuments)
      .filter(key => key.startsWith(`${serviceId}-${documentSlot}`))
      .length;

    const quantityIndex = existingUploads; // Use the current count as the index
    // Clear any existing upload errors for this quantity index
    const uploadKey = `${serviceId}-${documentSlot}-${quantityIndex}`;
    setUploadErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[uploadKey];
      return newErrors;
    });

    handleFileUpload(serviceId, documentSlot, documentType, file, quantityIndex);
    event.target.value = "";
  };

  const handleRemoveDocument = async (serviceId, documentSlot, quantityIndex = null, key) => {
    const uploadKey = key ? key : quantityIndex !== null
      ? `${serviceId}-${documentSlot}-${quantityIndex}`
      : `${serviceId}-${documentSlot}`;

    const doc = uploadedDocuments[uploadKey];

    if (doc?.id) {
      // Set loading state for this document
      setUploading((prev) => ({ ...prev, [uploadKey]: true }));

      try {
        const response = await documentsAPI.deleteDocument(doc.id);

        if (response.success) {
          toast.success("Document removed");

          // Remove from local state after successful deletion
          setUploadedDocuments((prev) => {
            const newDocs = { ...prev };
            delete newDocs[uploadKey];
            return newDocs;
          });

          // Refresh documents from server to ensure consistency
          if (location.state?.orderId) {
            const docsResponse = await documentsAPI.getForOrder(location.state?.orderId);
          }
        } else {
          // Handle response with success: false
          const errorMessage = response.message || "Failed to remove document";
          toast.error(errorMessage);
        }
      } catch (error) {
        // Extract error message - APIError extends Error, so error.message should work
        const errorMessage =
          error?.message ||
          error?.response?.data?.message ||
          "Failed to remove document";

        toast.error(errorMessage);
      } finally {
        // Remove loading state
        setUploading((prev) => {
          const newLoading = { ...prev };
          delete newLoading[uploadKey];
          return newLoading;
        });
      }
    } else {
      toast.error("Document not found");

      // Still remove from local state even if no ID
      setUploadedDocuments((prev) => {
        const newDocs = { ...prev };
        delete newDocs[uploadKey];
        return newDocs;
      });
    }
  };

  // Auto-scroll to top when page first loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleProceed = async () => {
    // Check if all required documents are uploaded based on quantity
    let allRequiredDocsUploaded = true;
    const missingDocs = [];

    for (const service of services) {
      const orderItem = order?.items?.find(item => item.service._id === service._id);
      const requiredQuantity = orderItem?.quantity || 1;

      const requiredDocs =
        service.requiredDocuments?.filter((doc) => doc.isRequired) || [];

      for (const doc of requiredDocs) {
        // Count uploaded documents for this service/document
        const uploadedCount = Object.keys(uploadedDocuments)
          .filter(key => key.startsWith(`${service._id}-${doc.name}`))
          .length;

        if (uploadedCount < requiredQuantity) {
          allRequiredDocsUploaded = false;
          missingDocs.push(`${doc.name} for ${service.name} (${uploadedCount}/${requiredQuantity} uploaded)`);
        }
      }
    }

    if (!allRequiredDocsUploaded) {
      toast.error(`Please upload: ${missingDocs.join(", ")}`);
      return;
    }

    setLoading(true);
    try {
      if (order) {
        // Submit for processing
        // try {
        //   await documentsAPI.submitOrder(order._id);
        //   toast.success("Documents submitted for processing!");
        //   navigate("/orders");
        // } catch (error) {
        //   toast.success("Documents uploaded successfully!");
        //   navigate("/orders");
        // }
        navigate(`/order/${order._id}`);
      } else {
        // Create new order
        const response = await ordersAPI.createOrder("cod");

        if (response.success) {
          toast.success("Order created successfully!");
          navigate("/orders");
        } else {
          toast.error(response.message || "Failed to create order");
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (services.length === 0) {
    return (
      <div className="upload-documents-page">
        <div className="upload-loading">
          <div className="loading-spinner"></div>
          <p>Loading service details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="upload-documents-page">
      <div className="upload-container">
        {/* Header */}
        <div className="upload-header">
          <button onClick={() => navigate(-1)} className="back-btn">
            ← Back
          </button>
          <h1>
            {location.state?.manageExisting
              ? "Manage Documents"
              : order
                ? "Upload Documents"
                : "Upload Required Documents"
            }
          </h1>
          <p>
            {location.state?.manageExisting
              ? `Manage documents for order ${order?.orderNumber || ''}`
              : order
                ? `Upload documents for order ${order.orderNumber}`
                : `Upload the required documents for ${services.length} service${services.length > 1 ? "s" : ""}`
            }
          </p>
        </div>

        {/* Services Info */}
        <div className="services-summary">
          {services.map((service) => (
            <div key={service._id} className="service-info-card">
              <div className="service-details">
                <h2>{service.name}</h2>
                <p>{service.shortDescription || service.description}</p>
              </div>
              <div className="service-meta">
                <span className="price">
                  ₹{service.discountPrice || service.price}
                </span>
                <span className="time">⏱️ {service.estimatedTime}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Document Upload Section */}
        <div className="documents-section">
          <div className="section-title">
            <h2>Required Documents</h2>
            <p>
              Please upload the following documents for each service. Ensure all
              files are in the correct format.
            </p>
          </div>

          {services.map((service) => {
            const orderItem = order?.items?.find(item => item.service._id === service._id);
            const maxQuantity = orderItem?.quantity || 1;

            return (
              <div key={service._id} className="service-documents">
                <div className="service-header">
                  <h3 className="service-name">{service.name}</h3>
                  <span className="quantity-badge">Quantity: {maxQuantity}</span>
                </div>

                <div className="documents-grid">
                  {service.requiredDocuments?.map((doc, index) => {
                    // Get all uploaded documents for this service/document
                    const uploadedDocs = Object.keys(uploadedDocuments)
                      .filter(key => key.startsWith(`${service._id}-${doc.name}`))
                      .map(key => uploadedDocuments[key]);
                
                    const uploadedCount = uploadedDocs.length;
                    const canUploadMore = uploadedCount < maxQuantity;

                    return (
                      <div key={index} className="document-card">
                        <div className="document-header">
                          <h4>{doc.name}</h4>
                          <div className="document-badges">
                            <span
                              className={`badge ${doc.isRequired ? "required" : "optional"}`}
                            >
                              {doc.isRequired ? "Required" : "Optional"}
                            </span>
                            <span className="quantity-info">
                              {uploadedCount}/{maxQuantity} uploaded
                            </span>
                          </div>
                        </div>

                        {doc.description && (
                          <p className="doc-description">{doc.description}</p>
                        )}

                        <div className="doc-requirements">
                          <span>
                            <strong>Formats:</strong>{" "}
                            {(doc.acceptedFormats || ["pdf"])
                              .join(", ")
                              .toUpperCase()}
                          </span>
                          <span>
                            <strong>Max Size:</strong> {doc.maxSize || 10}MB
                          </span>
                        </div>

                        {/* Show uploaded files */}
                        <div className="uploaded-files-list">
                          {uploadedDocs.map((uploadedDoc, idx) => {
                            // Use the same key format as loading documents
                            const uploadKey = uploadedDoc.id ?
                              `${uploadedDoc.serviceId}-${uploadedDoc.documentSlot}-${uploadedDoc.quantityIndex}` :
                              `${service._id}-${doc.name}-${idx}`;
                            const isUploading = uploading[uploadKey];

                            return (
                              <div key={idx} className="uploaded-file">
                                <div className="file-info">
                                  <span className="file-icon">📄</span>
                                  <div className="file-details">
                                    <span className="file-name">
                                      {uploadedDoc.name}
                                    </span>
                                    <span className="file-size">
                                      {formatFileSize(uploadedDoc.size)}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={() =>
                                    handleRemoveDocument(service._id, doc.name, idx, uploadKey)
                                  }
                                  className="remove-btn"
                                  title={isUploading ? "Removing file..." : "Remove file"}
                                  disabled={isUploading}
                                >
                                  {isUploading ? (
                                    <>
                                      <span className="spinner">⟳</span> Removing...
                                    </>
                                  ) : (
                                    <>
                                      <span>✕</span> Remove
                                    </>
                                  )}
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        {/* Show upload zone if more files can be uploaded */}
                        {canUploadMore && (
                          <div className="upload-zone">
                            <input
                              type="file"
                              id={`file-${service._id}-${doc.name.replace(/\s+/g, "-")}-${uploadedCount}`}
                              accept={(doc.acceptedFormats || ["pdf"])
                                .map((f) => `.${f}`)
                                .join(",")}
                              onChange={(e) =>
                                handleFileChange(
                                  service._id,
                                  doc.name,
                                  doc.type,
                                  e,
                                )
                              }
                              className="file-input"
                              disabled={uploading[`${service._id}-${doc.name}-${uploadedCount}`]}
                            />
                            <label
                              htmlFor={`file-${service._id}-${doc.name.replace(/\s+/g, "-")}-${uploadedCount}`}
                              className={`upload-label ${uploading[`${service._id}-${doc.name}-${uploadedCount}`] ? "uploading" : ""}`}
                            >
                              {uploading[`${service._id}-${doc.name}-${uploadedCount}`] ? (
                                <div className="uploading-state">
                                  <div className="upload-spinner"></div>
                                  <span>Uploading...</span>
                                </div>
                              ) : (
                                <div className="idle-state">
                                  <span className="upload-icon">📁</span>
                                  <span className="upload-text">
                                    Click to upload ({uploadedCount + 1}/{maxQuantity})
                                  </span>
                                  <span className="upload-hint">
                                    or drag and drop
                                  </span>
                                </div>
                              )}
                            </label>
                          </div>
                        )}

                        {/* Show upload errors */}
                        {Object.keys(uploadErrors)
                          .filter(key => key.startsWith(`${service._id}-${doc.name}`))
                          .map(key => (
                            <div key={key} className="upload-error">
                              {uploadErrors[key]}
                            </div>
                          ))}

                        {/* Show max quantity reached message */}
                        {!canUploadMore && uploadedCount >= maxQuantity && (
                          <div className="max-quantity-reached">
                            <span>✅ Maximum number of files uploaded</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action Bar */}
        <div className="action-bar">
          <button onClick={() => navigate(-1)} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleProceed}
            disabled={loading || Object.keys(uploadedDocuments).length === 0}
            className="btn-primary"
          >
            {loading ? (
              <>
                <span className="btn-spinner"></span>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>🚀</span>
                <span>{order ? "Finish & View Order" : "Complete Upload"}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadDocumentsPage;
